import { leaveBreadcrumb } from 'cfa-react-logging';
import authClient from '../authClient';
import ooeConstants from '../constants';
import { rexCateringClient } from '../shared/clients';
import { handleFetchFail, parseAsJson, throwIfNotOk } from '../util/httpUtils';
import { Customer, OrderReport, orderReportSchema, ResendReceiptEmailResponse } from './schemas';

export const getOrderReport = async ({
  orderId,
  locationNumber,
  cfaId,
}: {
  orderId: string;
  locationNumber: string;
  cfaId: string;
}): Promise<OrderReport> => {
  const baseUrl = ooeConstants.URL.ORDERS;
  const url = `${baseUrl}/reports/1.0/location/${locationNumber}/${orderId}/${cfaId}`;
  const method = 'GET';
  const bugsnag = {
    breadcrumb: `Get Order Report for Order ID ${orderId}`,
    errorClass: ooeConstants.BUGSNAG_ERRORCLASS_ORDERS,
    context: 'Get Order Report',
    info: {
      orderId,
      locationNumber,
      cfaId,
    },
  };

  leaveBreadcrumb(bugsnag.breadcrumb, {
    url,
    method,
    context: bugsnag.context,
    info: bugsnag.info,
  });

  const apiKey = await authClient.getOrRenewAccessToken();
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Authorization: `JWTBearer ${apiKey}`,
  };
  const response = await fetch(url, { method, headers }).catch(handleFetchFail);
  return orderReportSchema.parse(await parseAsJson(response));
};

export const getCustomerById = async ({ cfaId }: { cfaId: string }): Promise<Customer> => {
  leaveBreadcrumb(`Get Customer for CFA ID ${cfaId}`, {
    context: 'Get Customer By ID',
    info: { cfaId },
  });

  return rexCateringClient.getCustomerByID({ customerId: cfaId });
};

export const requestOrderPayment = async ({
  locationNumber,
  cfaId,
  orderId,
}: {
  orderId: string;
  locationNumber: string;
  cfaId: string;
}): Promise<void> => {
  const baseUrl = ooeConstants.URL.LOCATION_ORDERS;
  const url = `${baseUrl}/${locationNumber}/users/${cfaId}/orders/${orderId}/paymentRequest`;
  const method = 'POST';
  const bugsnag = {
    breadcrumb: `Request Order Payment for CFA ID ${cfaId} and Order ID ${orderId}`,
    errorClass: ooeConstants.BUGSNAG_ERRORCLASS_ORDERS,
    context: 'Request Order Payment',
    info: { orderId, locationNumber, cfaId },
  };

  leaveBreadcrumb(bugsnag.breadcrumb, {
    url,
    method,
    context: bugsnag.context,
    info: bugsnag.info,
  });

  const apiKey = await authClient.getOrRenewAccessToken();
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Authorization: `JWTBearer ${apiKey}`,
  };
  const response = await fetch(url, { method, headers }).catch(handleFetchFail);
  await throwIfNotOk(response);
};

export const resendReceiptEmail = async ({
  cfaId,
  orderId,
  isRegisteredMember,
}: {
  cfaId: string;
  orderId: string;
  isRegisteredMember: boolean;
}): Promise<ResendReceiptEmailResponse | undefined> => {
  const bugsnag = {
    breadcrumb: `Resending Receipt Email for CFA ID ${cfaId} and Order ID ${orderId}`,
    errorClass: ooeConstants.BUGSNAG_ERRORCLASS_ORDERS,
    context: 'Resend Receipt Email',
    info: { cfaId, orderId },
  };

  leaveBreadcrumb(bugsnag.breadcrumb, {
    context: bugsnag.context,
    info: bugsnag.info,
  });

  return rexCateringClient.resendReceiptEmail({ cfaId, orderId, isRegisteredMember });
};
